/**
 * Save Matched Patient Data Action
 *
 * Saves all consumer fields from a PATIENT_MATCHED response into context.
 * Used by both checkingForReturningPatient and sendingDisambiguate so both
 * paths populate identical context.
 */

import { assign } from "xstate";

/**
 * Normalize pcpAddress from the API.
 * The backend may return either a flat object or a double-nested one:
 *   Flat:   { streetAddress: "123 Main", city: "NY", state: "NY", zip: "10001" }
 *   Nested: { streetAddress: { streetAddress: "123 Main", city: "NY", state: "NY", zip: "10001" } }
 * This function always returns a flat { streetAddress, city, state, zip } or null.
 *
 * Backend fix: https://linear.app/healthcarerevolution/issue/PRI-270/user-service-double-nests-street-address
 */
export function normalizePcpAddress(pcpAddress: unknown): {
  streetAddress?: string;
  city?: string;
  state?: string;
  zip?: string;
} | null {
  if (!pcpAddress || typeof pcpAddress !== "object") return null;
  const addr = pcpAddress as Record<string, unknown>;

  if (Object.keys(addr).length === 0) return null;

  // Double-nested: pcpAddress.streetAddress is an object
  if (addr.streetAddress && typeof addr.streetAddress === "object") {
    const inner = addr.streetAddress as Record<string, unknown>;
    return {
      streetAddress: inner.streetAddress as string | undefined,
      city: inner.city as string | undefined,
      state: inner.state as string | undefined,
      zip: inner.zip as string | undefined,
    };
  }

  // Flat: pcpAddress.streetAddress is a string
  return {
    streetAddress: addr.streetAddress as string | undefined,
    city: addr.city as string | undefined,
    state: addr.state as string | undefined,
    zip: addr.zip as string | undefined,
  };
}

function isHipaaExpired(hipaaSignDate: string | null | undefined): boolean {
  if (!hipaaSignDate) return true;
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  return new Date(hipaaSignDate) < oneYearAgo;
}

export interface ActionMetadata {
  displayName: string;
  description: string;
  category: "context" | "logging" | "analytics" | "spawn" | "navigation";
}

export interface ActionWithMetadata {
  impl: ReturnType<typeof assign>;
  metadata: ActionMetadata;
}

export const saveMatchedPatientData: ActionWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
  impl: assign(({ event }: any) => {
    const output = event.output as {
      userId?: string;
      consumerId?: string;
      consumer?: {
        firstname?: string;
        lastname?: string;
        sex?: string;
        streetAddress?: string;
        city?: string;
        state?: string;
        zip?: string;
        addressLine2?: string;
        email?: string;
        ehrPatientId?: string;
        insurances?: unknown[];
        hipaaSignDate?: string;
        emergencyContactFirstName?: string;
        emergencyContactLastName?: string;
        emergencyContactPhone?: string;
        emergencyContactRelationship?: string;
        pcpFirstName?: string;
        pcpLastName?: string;
        pcpPhone?: string;
        pcpEhrId?: string;
        pcpAddress?: {
          streetAddress?: string;
          city?: string;
          state?: string;
          zip?: string;
        };
        pharmacyName?: string;
        pharmacyPhone?: string;
        pharmacyEhrId?: string;
        pharmacyNcpdpId?: string;
        pharmacyAddress?: {
          streetAddress?: string;
          city?: string;
          state?: string;
          zip?: string;
        };
        referralSource?: string;
      };
    };
    const consumer = output.consumer;
    const normalizedPcpAddr = normalizePcpAddress(consumer?.pcpAddress);
    return {
      userId: output.userId,
      consumerId: output.consumerId,
      ehrPatientId: consumer?.ehrPatientId,
      firstName: consumer?.firstname ?? undefined,
      lastName: consumer?.lastname ?? undefined,
      sex: consumer?.sex ?? undefined,
      _consumerAddress: consumer?.streetAddress,
      _consumerCity: consumer?.city,
      _consumerState: consumer?.state,
      _consumerZip: consumer?.zip,
      _consumerAddressLine2: consumer?.addressLine2,
      email: consumer?.email,
      _isReturningPatient: true,
      _consumerInsurancesFromMatch: consumer?.insurances ?? null,
      _hipaaExpired: isHipaaExpired(consumer?.hipaaSignDate),
      _consumerPharmacyName: consumer?.pharmacyName,
      _consumerPharmacyPhone: consumer?.pharmacyPhone,
      _consumerPharmacyStreetAddress: consumer?.pharmacyAddress?.streetAddress,
      _consumerPharmacyCity: consumer?.pharmacyAddress?.city,
      _consumerPharmacyState: consumer?.pharmacyAddress?.state,
      _consumerPharmacyZip: consumer?.pharmacyAddress?.zip,
      _consumerPharmacyEhrId: consumer?.pharmacyEhrId,
      _consumerPharmacyNcpdpId: consumer?.pharmacyNcpdpId,
      _consumerPcpFirstName: consumer?.pcpFirstName,
      _consumerPcpLastName: consumer?.pcpLastName,
      _consumerPcpPhone: consumer?.pcpPhone,
      _consumerPcpEhrId: consumer?.pcpEhrId,
      _consumerPcpAddress: normalizedPcpAddr?.streetAddress,
      _consumerPcpCity: normalizedPcpAddr?.city,
      _consumerPcpState: normalizedPcpAddr?.state,
      _consumerPcpZip: normalizedPcpAddr?.zip,
      _consumerEmergencyContactFirstName:
        consumer?.emergencyContactFirstName ?? undefined,
      _consumerEmergencyContactLastName:
        consumer?.emergencyContactLastName ?? undefined,
      _consumerEmergencyContactPhone: consumer?.emergencyContactPhone,
      _consumerEmergencyContactRelationship:
        consumer?.emergencyContactRelationship ?? undefined,
      _consumerReferralSource: consumer?.referralSource,
    };
  }),
  metadata: {
    displayName: "saveMatchedPatientData",
    description:
      "Save all consumer fields from a PATIENT_MATCHED response into context",
    category: "context",
  },
};
